import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Volume2, VolumeX, Speaker, Bluetooth, HardDrive, Smartphone, Radio, Music, Headphones, Home, Bed, Coffee, Leaf } from "lucide-react";

export default function SoundSystems() {
  const { t, i18n } = useTranslation();
  const dir = i18n.language === "ar" ? "rtl" : "ltr";

  // State for volume control for each zone, plus mute handling
  const [volumes, setVolumes] = React.useState({ 0: 60, 1: 70, 2: 80, 3: 90 });
  const [muted, setMuted] = React.useState({ 0: false, 1: false, 2: false, 3: false });
  const [prevVolumes, setPrevVolumes] = React.useState({});

  const handleVolumeChange = (index, newVolume) => {
    // update volume; if previously muted and user moves slider, unmute
    setVolumes(prev => ({ ...prev, [index]: newVolume }));
    if (muted[index]) {
      setMuted(prev => ({ ...prev, [index]: false }));
    }
  };

  const toggleMute = (index) => {
    setMuted((prev) => {
      const isMuted = !prev[index];
      if (isMuted) {
        // store previous volume then set volume to 0
        setPrevVolumes(p => ({ ...p, [index]: volumes[index] }));
        setVolumes(v => ({ ...v, [index]: 0 }));
      } else {
        // restore previous volume or default
        setVolumes(v => ({ ...v, [index]: prevVolumes[index] ?? 60 }));
      }
      return { ...prev, [index]: isMuted };
    });
  };

  const features = [
    { icon: Speaker, key: "highQualitySpeakers", color: "#ffde21" },
    { icon: Music, key: "audioSources", color: "#ffde21" },
    { icon: Radio, key: "zoneControl", color: "#ffde21" },
    { icon: Smartphone, key: "mobileApp", color: "#ffde21" }
  ];

  const audioSources = [
    { icon: Bluetooth, title: "Bluetooth", desc: dir === "rtl" ? "اتصال لاسلكي سريع" : "Fast wireless connection" },
    { icon: HardDrive, title: dir === "rtl" ? "قرص صلب" : "Hard Drive", desc: dir === "rtl" ? "تخزين محلي للموسيقى" : "Local music storage" },
    { icon: Smartphone, title: dir === "rtl" ? "تطبيق الهاتف" : "Mobile App", desc: dir === "rtl" ? "تحكم كامل" : "Full control" }
  ];

  return (
    <div dir={dir} className="min-h-screen" style={{ background: '#fafcf0' }}>
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background Pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #2d2d2b 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="flex flex-col md:flex-row items-center gap-12 md:gap-16">
            {/* Image Side */}
            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="md:w-1/2"
            >
              <div className="relative">
                <div
                  className="absolute -inset-4 rounded-3xl opacity-20 blur-2xl"
                  style={{ background: '#ffde21' }}
                ></div>
                <img
                  src="/images/SoundSystem.webp"
                  alt={t("soundSystems")}
                  className="relative rounded-3xl shadow-2xl w-full object-cover"
                  style={{ border: '3px solid #ffde21' }}
                  fetchpriority="high"
                  decoding="async"
                />
              </div>
            </motion.div>

            {/* Text Side */}
            <motion.div
              initial={{ opacity: 0, x: dir === "rtl" ? 50 : -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="md:w-1/2"
            >
              <div className="inline-block mb-4">
                <span
                  className="px-4 py-2 rounded-full text-sm font-bold flex items-center gap-2"
                  style={{
                    background: 'rgba(255, 222, 33, 0.2)',
                    color: '#2d2d2b'
                  }}
                >
                  <Volume2 size={16} />
                  {dir === "rtl" ? "صوت عالي الجودة" : "High Quality Sound"}
                </span>
              </div>

              <h1
                className="text-4xl md:text-5xl lg:text-6xl font-black mb-6 leading-tight"
                style={{ color: '#2d2d2b' }}
              >
                {t("soundSystems")}
              </h1>

              <p
                className="text-lg md:text-xl leading-relaxed mb-8"
                style={{ color: '#2d2d2b', opacity: 0.8 }}
              >
                {dir === "rtl"
                  ? "أنظمة صوتية متطورة توفر تجربة استماع استثنائية في كل ركن من أركان منزلك أو مكتبك"
                  : "Advanced sound systems providing an exceptional listening experience in every corner of your home or office"}
              </p>

              <Link to="/design-home">
                <button
                  className="px-8 py-4 rounded-xl font-bold text-lg transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                  style={{
                    background: '#ffde21',
                    color: '#2d2d2b'
                  }}
                >
                  {dir === "rtl" ? "احصل على عرض سعر" : "Get a Quote"}
                </button>
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "مميزات النظام" : "System Features"}
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {dir === "rtl"
                ? "تقنيات صوتية متقدمة لتجربة استماع لا تُنسى"
                : "Advanced audio technologies for an unforgettable listening experience"}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative p-8 rounded-2xl transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl group cursor-pointer"
                  style={{
                    background: 'white',
                    border: '2px solid rgba(255, 222, 33, 0.2)'
                  }}
                >
                  {/* Icon Circle */}
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-6"
                    style={{ background: '#ffde21' }}
                  >
                    <Icon size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
                  </div>

                  {/* Feature Description */}
                  <p
                    className="text-base leading-relaxed"
                    style={{ color: '#2d2d2b' }}
                  >
                    {t(`soundSystems_features.${feature.key}`)}
                  </p>

                  {/* Decorative Element */}
                  <div
                    className="absolute bottom-0 left-0 right-0 h-1 rounded-b-2xl transition-all duration-300 group-hover:h-2"
                    style={{ background: '#ffde21' }}
                  ></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Audio Sources Section */}
      <section
        className="py-16 md:py-24 relative overflow-hidden"
        style={{ background: '#2d2d2b' }}
      >
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21),
                             linear-gradient(45deg, #ffde21 25%, transparent 25%, transparent 75%, #ffde21 75%, #ffde21)`,
            backgroundSize: '60px 60px',
            backgroundPosition: '0 0, 30px 30px'
          }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#ffde21' }}
            >
              {dir === "rtl" ? "مصادر الصوت المتعددة" : "Multiple Audio Sources"}
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: 'white', opacity: 0.9 }}
            >
              {dir === "rtl"
                ? "استمتع بموسيقاك من مصادر متنوعة وبجودة عالية"
                : "Enjoy your music from various sources with high quality"}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {audioSources.map((source, index) => {
              const Icon = source.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative p-8 rounded-2xl text-center transition-all duration-300 hover:-translate-y-2 hover:shadow-2xl group"
                  style={{
                    background: 'rgba(250, 252, 240, 0.05)',
                    border: '2px solid rgba(255, 222, 33, 0.3)'
                  }}
                >
                  <div
                    className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 transition-transform duration-300 group-hover:scale-110"
                    style={{ background: '#ffde21' }}
                  >
                    <Icon size={36} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
                  </div>
                  <h3
                    className="text-xl font-bold mb-2"
                    style={{ color: 'white' }}
                  >
                    {source.title}
                  </h3>
                  <p style={{ color: 'white', opacity: 0.8 }}>
                    {source.desc}
                  </p>

                  {/* Corner Accent */}
                  <div
                    className="absolute top-4 right-4 w-2 h-2 rounded-full"
                    style={{ background: '#ffde21', opacity: 0.5 }}
                  ></div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Zone Control Visualization */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "التحكم في كل منطقة" : "Control Every Zone"}
            </h2>
            <p
              className="text-lg max-w-2xl mx-auto"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {dir === "rtl"
                ? "تحكم منفصل في مستوى الصوت والموسيقى لكل غرفة"
                : "Independent control of volume and music for each room"}
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { room: dir === "rtl" ? "غرفة المعيشة" : "Living Room", icon: Home },
              { room: dir === "rtl" ? "غرفة النوم" : "Bedroom", icon: Bed },
              { room: dir === "rtl" ? "المطبخ" : "Kitchen", icon: Coffee },
              { room: dir === "rtl" ? "الحديقة" : "Garden", icon: Leaf }
            ].map((zone, index) => {
              const ZoneIcon = zone.icon;
              const gradientDir = dir === 'rtl' ? 'to left' : 'to right';
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="p-6 rounded-2xl text-center transition-all duration-300 hover:scale-105 hover:shadow-xl"
                  style={{
                    background: 'white',
                    border: '2px solid rgba(255, 222, 33, 0.3)'
                  }}
                >
                  <div className="text-5xl mb-4">
                    <ZoneIcon size={36} />
                  </div>
                  <h3
                    className="text-lg font-bold mb-3"
                    style={{ color: '#2d2d2b' }}
                  >
                    {zone.room}
                  </h3>
                  <div className="space-y-2">
                    <div
                      className="flex items-center justify-between gap-2"
                      style={{ color: '#2d2d2b' }}
                    >
                      <button onClick={() => toggleMute(index)} className="p-2 rounded-md bg-transparent hover:bg-gray-100">
                        {muted[index] ? <VolumeX size={18} /> : <Volume2 size={18} />}
                      </button>
                      <span className="text-sm font-bold">{volumes[index]}%</span>
                    </div>
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={volumes[index]}
                      onChange={(e) => handleVolumeChange(index, parseInt(e.target.value))}
                      className="w-full h-2 rounded-full appearance-none cursor-pointer"
                      style={{
                        background: `linear-gradient(${gradientDir}, #ffde21 0%, #ffde21 ${volumes[index]}%, rgba(255, 222, 33, 0.2) ${volumes[index]}%, rgba(255, 222, 33, 0.2) 100%)`,
                        outline: 'none'
                      }}
                    />
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 md:py-24" style={{ background: 'rgba(255, 222, 33, 0.05)' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2
              className="text-3xl md:text-4xl font-black mb-4"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "لماذا أنظمة الصوت الذكية؟" : "Why Smart Sound Systems?"}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl"
              style={{
                background: 'white',
                border: '2px solid rgba(255, 222, 33, 0.3)'
              }}
            >
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                style={{
                  background: 'rgba(255, 222, 33, 0.2)',
                  border: '2px solid #ffde21'
                }}
              >
                <Headphones size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
              </div>
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: '#2d2d2b' }}
              >
                {dir === "rtl" ? "جودة صوت استثنائية" : "Exceptional Sound Quality"}
              </h3>
              <p style={{ color: '#2d2d2b', opacity: 0.8 }}>
                {dir === "rtl"
                  ? "سماعات عالية الجودة توفر تجربة صوتية غامرة"
                  : "High-quality speakers providing an immersive audio experience"}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl"
              style={{
                background: 'white',
                border: '2px solid rgba(255, 222, 33, 0.3)'
              }}
            >
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                style={{
                  background: 'rgba(255, 222, 33, 0.2)',
                  border: '2px solid #ffde21'
                }}
              >
                <Radio size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
              </div>
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: '#2d2d2b' }}
              >
                {dir === "rtl" ? "تحكم مرن" : "Flexible Control"}
              </h3>
              <p style={{ color: '#2d2d2b', opacity: 0.8 }}>
                {dir === "rtl"
                  ? "تحكم منفصل لكل منطقة يمنحك حرية كاملة"
                  : "Separate control for each zone gives you complete freedom"}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="p-8 rounded-2xl"
              style={{
                background: 'white',
                border: '2px solid rgba(255, 222, 33, 0.3)'
              }}
            >
              <div
                className="w-16 h-16 rounded-xl flex items-center justify-center mb-6"
                style={{
                  background: 'rgba(255, 222, 33, 0.2)',
                  border: '2px solid #ffde21'
                }}
              >
                <Smartphone size={32} style={{ color: '#2d2d2b' }} strokeWidth={2.5} />
              </div>
              <h3
                className="text-xl font-bold mb-3"
                style={{ color: '#2d2d2b' }}
              >
                {dir === "rtl" ? "سهولة الاستخدام" : "Easy to Use"}
              </h3>
              <p style={{ color: '#2d2d2b', opacity: 0.8 }}>
                {dir === "rtl"
                  ? "تطبيق هاتف بسيط للتحكم من أي مكان"
                  : "Simple mobile app for control from anywhere"}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16 md:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <Volume2 size={60} style={{ color: '#ffde21', margin: '0 auto', marginBottom: '1.5rem' }} />
            <h2
              className="text-3xl md:text-4xl font-black mb-6"
              style={{ color: '#2d2d2b' }}
            >
              {dir === "rtl" ? "جاهز لتجربة صوتية مميزة؟" : "Ready for a Premium Audio Experience?"}
            </h2>
            <p
              className="text-lg md:text-xl mb-8"
              style={{ color: '#2d2d2b', opacity: 0.7 }}
            >
              {dir === "rtl"
                ? "احصل على نظام صوتي متطور يملأ منزلك بالموسيقى عالية الجودة"
                : "Get an advanced sound system that fills your home with high-quality music"}
            </p>
            <Link to="/contact">
              <button
                className="px-10 py-5 rounded-xl font-bold text-xl transition-all duration-300 hover:shadow-2xl hover:-translate-y-1"
                style={{
                  background: '#ffde21',
                  color: '#2d2d2b'
                }}
              >
                {t("homepage.buttons.contactUs")}
              </button>
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}